import { useEffect, useState } from 'preact/hooks';
import {
  getRecordTypeSchema,
  listAllRecordsWithLinks,
  listHouseholdPerformers,
  type HouseholdPerformer,
  type RecordRow,
  type RecordTypeSchema,
} from '../lib/api';
import { useRecordsVersion } from '../lib/recordEditor';
import { activeOnly } from '../lib/retired';
import { describeReadFailure, type ReadFailure } from '../lib/readFailure';
import { humanizeValue } from '../lib/fieldDisplay';
import { Strip } from '../components/Strip';
import {
  AA,
  LINK,
  ageWords,
  auditionAge,
  auditionFallbackName,
  otherEnd,
  seasonEpisode,
  spineFromLifecycle,
} from '../lib/auditionSpread';
import { comingUpDayLabel, dayKeyOf } from '../lib/calendarItems';
import { isClosedSubmission, isOpenSubmission } from '../lib/submissions';
import { SubmissionRow, SubmissionsListBody, useSubmissionLines } from './HouseholdSubmissions';
import { SubmissionStartSheet } from '../components/SubmissionStartSheet';
import './spread.css';

// A4 — AUDITIONS: the live pipeline, and the door to the spread.
//
// This replaces the thin Registry declaration for the `auditions` tab. The
// difference that matters is not the layout — it is that a row here OPENS THE
// AUDITION, not the record editor. Until now the only way into an audition
// was a schema-driven form, which is the fallback door, never the front one.
//
// ===================================================================
// THE STAGE STRIP IS THE PACK'S OWN VOCABULARY, FETCHED
// ===================================================================
// routes/route.ts carries an AUDITION_STAGES list — Self-submitted, Audition,
// Callback, … — and it belongs to the OLD actor pack. Three of its ids
// (`self-submitted`, `audition`, `booked` as a stage) do not exist in
// aa_audition's lifecycle at all. Offering it here would filter on states
// that are not there and quietly return nothing.
//
// So the strip is built from `schema.lifecycle.states` at read time. A stage
// added to the pack appears here the day it ships; one removed stops being
// offered. No list of state names lives in this file.
//
// The strip shows the ADVANCEMENT LINE plus whichever off-spine states the
// household actually has records in — an "Archived" chip on a household that
// has never archived anything is a filter for an empty room.
//
// ===================================================================
// B21 (2026-09-27) — SUBMISSIONS LIVE HERE TOO, NOT ON A TAB OF THEIR OWN
// ===================================================================
// Huan's ruling, moving this off the Submissions tab: "we should try and
// incorporate [submissions] into the same page as auditions somehow. Don't
// want to push projects off the rails." So the strip gains a leading
// "Submissions" chip (`items` below), "All" carries a compact preview of the
// open ones (`SubmissionsCompactGroup`), and both reuse `HouseholdSubmissions
// .tsx`'s own read + row markup (`useSubmissionLines`/`SubmissionRow`) rather
// than a second copy of either. `stage` stays exactly what it always was —
// LOCAL component state, never the route — `initialFilter` only seeds it
// once, from an old `/submissions` link or a "See all" tap elsewhere.

export interface HouseholdAuditionsProps {
  tenantId: string;
  onOpen: (auditionId: string) => void;
  /** B21 — a row in the merged Submissions filter/group opens the
   * submission's own spread, not an audition. Optional so a test that only
   * cares about auditions keeps mounting this screen unchanged; App.tsx
   * always supplies it. */
  onOpenSubmission?: (submissionId: string) => void;
  /** B21 — which chip is selected on mount. Local state, same as `stage`
   * itself (see this file's own header): App.tsx reads this once from the
   * URL (`#/auditions/submissions`) so an old `/submissions` link or a
   * "All submissions →" tap lands on the right chip; nothing keeps it in
   * sync with the URL after that, same as every other stage here never has. */
  initialFilter?: string;
}

interface Line {
  audition: RecordRow;
  roleName: string | null;
  performer: HouseholdPerformer | null;
}

export function HouseholdAuditions({
  tenantId,
  onOpen,
  onOpenSubmission,
  initialFilter,
}: HouseholdAuditionsProps) {
  const [lines, setLines] = useState<Line[] | null>(null);
  const [schema, setSchema] = useState<RecordTypeSchema | null>(null);
  const [failure, setFailure] = useState<ReadFailure | null>(null);
  const [stage, setStage] = useState<string>(initialFilter ?? 'all');
  const [retryNonce, setRetryNonce] = useState(0);
  const recordsVersion = useRecordsVersion();
  // B21 §2 — the compact group needs the same read the Submissions filter's
  // own list body makes, and reading it here (once, off this screen's own
  // effect) is what lets both the "All" group and the "+ Submit for
  // something" start sheet exist without the audition list waiting on it.
  const openSubmission = onOpenSubmission ?? onOpen;
  const submissionLines = useSubmissionLines(tenantId);
  const [startingSubmission, setStartingSubmission] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setFailure(null);
      try {
        const [auditionSchema, auds, allRoles, performers] = await Promise.all([
          getRecordTypeSchema(tenantId, AA.AUDITION).catch(() => null),
          listAllRecordsWithLinks(tenantId, AA.AUDITION, {
            linkTypes: [LINK.AUDITION_ROLE, LINK.AUDITION_SUBJECT],
          }),
          listAllRecordsWithLinks(tenantId, AA.ROLE, { linkTypes: [LINK.ROLE_PROJECT] }),
          listHouseholdPerformers(tenantId).catch(() => [] as HouseholdPerformer[]),
        ]);
        if (cancelled) return;

        const roleName = new Map(activeOnly(allRoles.records).map((r) => [r.id, r.display_name]));
        const byPerformer = new Map(performers.map((p) => [p.id, p]));

        const next: Line[] = activeOnly(auds.records).map((audition) => {
          const links = auds.linksByRecordId[audition.id] ?? [];
          const roleId = otherEnd(links, audition.id, LINK.AUDITION_ROLE);
          const sid = otherEnd(links, audition.id, LINK.AUDITION_SUBJECT);
          return {
            audition,
            roleName: roleId ? roleName.get(roleId) ?? null : null,
            performer: sid ? byPerformer.get(sid) ?? null : null,
          };
        });
        // Reverse chronological on when it went out, then on creation for the
        // ones that have not gone out yet — those are the ones being prepared,
        // and they belong at the top.
        next.sort((a, b) => {
          const ka = String(a.audition.fields.submitted_at ?? a.audition.created_at);
          const kb = String(b.audition.fields.submitted_at ?? b.audition.created_at);
          return ka < kb ? 1 : -1;
        });
        setSchema(auditionSchema);
        setLines(next);
      } catch (err) {
        if (cancelled) return;
        setFailure(describeReadFailure(err));
        setLines(null);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [tenantId, retryNonce, recordsVersion]);

  if (failure) {
    return (
      <div class="sp">
        <div class="sp__eyebrow">Auditions</div>
        <div class="sp__say">
          {failure.headline}
          <br />
          <span class="sp__say-soft">{failure.detail}</span>
        </div>
        {failure.retryable ? (
          <button type="button" class="sp__retry" onClick={() => setRetryNonce((n) => n + 1)}>
            Read the auditions again
          </button>
        ) : null}
      </div>
    );
  }

  if (lines === null) {
    return (
      <div class="sp">
        <div class="sp__eyebrow">Auditions</div>
        <div class="sp__say">One moment…</div>
      </div>
    );
  }

  const { spine, offSpine } = spineFromLifecycle(schema?.lifecycle ?? undefined);
  const present = new Set(lines.map((l) => l.audition.lifecycle_state ?? ''));
  const items = [
    { id: 'all', label: 'All' },
    // B21 §2 — LEADING, before the audition stages: "All · Submissions ·
    // Requested · …". Unlike the stage chips after it, not gated on
    // `present` — the household's own funnel is offered whether or not
    // anything is in it yet, the same as "All" itself always is.
    { id: 'submissions', label: 'Submissions' },
    ...spine.filter((s) => present.has(s)).map((s) => ({ id: s, label: humanizeValue(s) })),
    ...offSpine.filter((s) => present.has(s)).map((s) => ({ id: s, label: humanizeValue(s) })),
  ];
  const shown = stage === 'all' ? lines : lines.filter((l) => l.audition.lifecycle_state === stage);

  return (
    <div class="sp">
      <div class="sp__eyebrow">Auditions</div>
      {items.length > 1 ? <Strip items={items} activeId={stage} onSelect={(s) => setStage(s)} /> : null}

      {stage === 'submissions' ? (
        // B21 §2 — "reusing HouseholdSubmissions.tsx's list": the identical
        // body (its own secondary Drafts/Sent/Invited/Closed strip, the
        // list, "+ Submit for something"), not a second copy of it.
        <SubmissionsListBody tenantId={tenantId} onOpen={openSubmission} />
      ) : (
        <>
          {stage === 'all' ? (
            <SubmissionsCompactGroup
              submissionLines={submissionLines}
              onOpen={openSubmission}
              onSeeAll={() => setStage('submissions')}
              onStart={() => setStartingSubmission(true)}
            />
          ) : null}

          {shown.length === 0 ? (
            // A statement of fact. The shell's persistent "+" is how anything is
            // created; a list that explains the button is a list admitting the
            // button does not teach itself.
            <div class="sp__empty">
              {lines.length === 0
                ? 'Nothing out right now. Hand Clappy a breakdown — a photo, a PDF, or the text — and the read lands here.'
                : 'Nothing at this stage.'}
            </div>
          ) : (
            <div class="sp__list">
              {shown.map((line) => {
                const rawAge = auditionAge(line.audition);
                // B15 — a 'sent' age carries a moment, not a day count; its
                // words are the same day formatter Today's "Coming up" uses
                // (auditionSpread.ts's own AuditionAge comment on why this file
                // formats it rather than that one).
                const age =
                  rawAge?.kind === 'sent' ? `Sent ${comingUpDayLabel(dayKeyOf(rawAge.at))}` : ageWords(rawAge);
                const se = seasonEpisode(line.audition.fields);
                const modality = line.audition.fields.modality;
                return (
                  <button
                    type="button"
                    key={line.audition.id}
                    class="sp__row"
                    onClick={() => onOpen(line.audition.id)}
                  >
                    <span class="sp__row-top">
                      {/* The part is the name of the row; the audition's own
                          display name is a modality and a timestamp, which is
                          what the record is, not what it is about. */}
                      <span class="sp__row-name">{line.roleName ?? auditionFallbackName(line.audition)}</span>
                      <span class="sp__row-when">
                        {line.audition.lifecycle_state ? humanizeValue(line.audition.lifecycle_state) : ''}
                      </span>
                    </span>
                    <span class="sp__row-sub">
                      {/* WHOSE IT IS, INLINE — part of the item, never a group
                          heading. Grouping by child would rebuild the subject
                          switcher the household home exists to avoid.

                          ⚠ THE SEPARATOR IS INTERLEAVED, NEVER PREFIXED. Each
                          piece used to carry its own leading " · ", which reads
                          correctly only while the FIRST piece is present. A
                          captured audition has no performer yet — the model may
                          not mint a subject, by design — so the card rendered
                          "· Self Tape · due in 10 days", opening on a dangling
                          dot. Seen on a real phone 2026-08-29; no suite noticed,
                          because every fixture had a performer. */}
                      {[
                        line.performer ? <b>{line.performer.name}</b> : null,
                        typeof modality === 'string' && modality ? humanizeValue(modality) : null,
                        se || null,
                        age || null,
                      ]
                        .filter(Boolean)
                        .flatMap((part, i) => (i === 0 ? [part] : [' · ', part]))}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {startingSubmission ? (
        <SubmissionStartSheet
          tenantId={tenantId}
          onClose={() => setStartingSubmission(false)}
          onCreated={(id) => {
            setStartingSubmission(false);
            openSubmission(id);
          }}
        />
      ) : null}
    </div>
  );
}

/** B21 §2 — the compact group "All" carries above the audition list: up to
 * 3 OPEN submissions (draft/sent/invited), newest first, each rendered by
 * the identical `SubmissionRow` the full Submissions filter uses; then
 * "All submissions (n) →" once there is more to see (more than 3 open, or
 * any closed at all — closed ones never show here, so the door has to
 * stay reachable); then "+ Submit for something", always. With nothing
 * submitted at all, the group is just that one line — never an empty
 * "Submissions" heading over nothing. */
function SubmissionsCompactGroup({
  submissionLines,
  onOpen,
  onSeeAll,
  onStart,
}: {
  submissionLines: ReturnType<typeof useSubmissionLines>;
  onOpen: (id: string) => void;
  onSeeAll: () => void;
  onStart: () => void;
}) {
  const { lines } = submissionLines;
  // The read hasn't answered yet — say nothing rather than a wrong "no
  // submissions" for a beat (the same posture the shoot-day band takes,
  // this file's own header: an addition to the screen, never rendered
  // wrong while it loads).
  if (lines === null) return null;

  const startButton = (
    <button type="button" class="sp__act" data-testid="submissions-start" onClick={onStart}>
      + Submit for something
    </button>
  );

  if (lines.length === 0) {
    return <div class="sp__acts">{startButton}</div>;
  }

  const open = lines.filter((l) => isOpenSubmission(l.submission.lifecycle_state));
  const shown = open.slice(0, 3);
  const hasClosed = lines.some((l) => isClosedSubmission(l.submission.lifecycle_state));
  const seeAll = open.length > shown.length || hasClosed;

  return (
    <>
      <div class="sp__sec">Submissions</div>
      {shown.map((line) => (
        <SubmissionRow key={line.submission.id} line={line} onOpen={onOpen} />
      ))}
      {seeAll ? (
        <button type="button" class="sp__retry" onClick={onSeeAll}>
          {`All submissions (${lines.length}) →`}
        </button>
      ) : null}
      <div class="sp__acts">{startButton}</div>
    </>
  );
}
